import {Directive,ElementRef,forwardRef,HostListener,Input,Inject,LOCALE_ID} from '@angular/core';
import {DecimalPipe,getLocaleNumberSymbol,NumberSymbol} from '@angular/common';
import {FormControl,FormGroupDirective,NG_VALUE_ACCESSOR,NgForm} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {MAT_LEGACY_INPUT_VALUE_ACCESSOR as MAT_INPUT_VALUE_ACCESSOR} from '@angular/material/legacy-input';

export class InputNumberErrorStateMatcher implements ErrorStateMatcher {
	/**
	 * Vérification de l'état du champ
	 */
	isErrorState(control: FormControl | null,form: FormGroupDirective | NgForm | null): boolean {
		//Vérification de la présence d'erreurs
		return control && control.invalid && (control.dirty || control.touched);
	}
}

/**
 * Gestionnaire d'erreur pour la saisie d'un nombre borné
 */
export class InputRangeErrorStateMatcher implements ErrorStateMatcher {
	/** Valeur courante */
	private value: number | null;

	/**
	 * Constructeur
	 *
	 * @param min Borne mininum
	 * @param max born maximum
	 */
	constructor(private readonly min: number | null,private readonly max: number | null) {
	}

	/**
	 * Vérification de l'état du champ
	 */
	isErrorState(control: FormControl | null,form: FormGroupDirective | NgForm | null): boolean {
		let isError: boolean;

		//Vérification de la présence d'erreurs
		isError = control && control.invalid;

		//S'il n'y a pas encore d'erreur on vérifie la borne
		if (!isError && (this.min !== null || this.max !== null)) {
			//Récupération et parse de la valeur
			this.value = typeof control.value === 'string' ? Number(control.value.replace(/[^,\d.-]/g,'').replace(/[,]/g,'.')) : control.value;

			//Vérification que la valeur est bien un nombre
			if (!isNaN(this.value)) {
				//Vérification borne inférieure et supérieure
				isError = (this.min !== null && this.value < this.min) || (this.max !== null && this.value > this.max)
			} else {
				isError = true;
			}
		}

		return isError;
	}
}

@Directive({
	selector: '[matInput][nNumber]',
	providers: [{
		provide: MAT_INPUT_VALUE_ACCESSOR,
		useExisting: InputNumberDirective
	},{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => InputNumberDirective),
		multi: true
	},{
		provide: ErrorStateMatcher,
		useClass: InputNumberErrorStateMatcher
	}]
})
export class InputNumberDirective {
	/** Nombre de décimales */
	@Input() nNumber?: number = 0;

	/** Alignement horizontal de la valeur dans l'input */
	@Input() align?: 'left' | 'center' | 'right' = 'right';

	/** Valeur actuelle */
	private _value: string | null;

	/** Indique si le champ a le focus */
	private isFocused = false;

	/**
	 * Constructeur
	 */
	constructor(
		private elementRef: ElementRef<HTMLInputElement>,
		private decimalPipe: DecimalPipe,
		@Inject(LOCALE_ID) private localeId: string
	) {
		//Ajout de la classe
		if (this.align) {
			elementRef.nativeElement.classList.add('text-' + this.align);
		}
	}

	/**
	 * Lecture de la valeur
	 */
	get value(): string | null {
		//Retour de la valeur
		return this._value;
	}

	/**
	 * Définition de la valeur
	 */
	@Input('value')
	set value(value: string | null) {
		//Mise à jour de la valeur
		this._value = value || null;

		//Formattage de la valeur
		this.formatValue(value);
	}

	/**
	 * Formattage de la valeur
	 */
	private formatValue(value: string | null) {
		//Si le champ a le focus, on vérifie si la valeur reçue est identique à celle actuellement affichée pour éviter d'écraser le curseur de saisie
		if (this.isFocused) {
			const currentVal = this.elementRef.nativeElement.value;
			const decimalSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Decimal);
			const cleanCurrent = currentVal ? currentVal.split(decimalSymbol).join('.') : '';
			const cleanNew = value !== null && value !== undefined ? value.toString().replace(/[,]/g,'.') : '';

			if (cleanCurrent && cleanNew && !isNaN(Number(cleanCurrent)) && !isNaN(Number(cleanNew)) && Number(cleanCurrent) === Number(cleanNew)) {
				//C'est le même nombre, on ne met pas à jour l'élément HTML pour ne pas déplacer le curseur d'édition
				return;
			}
		}

		let formatedValue = '';

		if (value !== null && value !== undefined) {
			const replacedValue = value.toString().replace(/[,]/g,'.');

			if (!isNaN(Number(replacedValue))) {
				if (this.isFocused) {
					//Si focus, on garde la valeur d'édition sans séparateurs de milliers
					const decimalSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Decimal);
					formatedValue = replacedValue.replace(/[.]/g, decimalSymbol);
				} else {
					//Définition de la valeur
					formatedValue = this.decimalPipe.transform(replacedValue, `1.${this.nNumber || 0}-${this.nNumber || 0}`);
				}
			}
		}

		this.updateElementInput(formatedValue);

		//Déclenchement de la modification
		let parsedValue: number | null = null;
		if (formatedValue) {
			const decimalSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Decimal);
			const groupSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Group);
			let cleanValue = formatedValue.split(groupSymbol).join('');
			cleanValue = cleanValue.split(decimalSymbol).join('.');
			cleanValue = cleanValue.replace(/[^0-9.-]/g,'');
			const num = Number(cleanValue);
			parsedValue = isNaN(num) ? null : num;
		}
		this.onChange(parsedValue);
	}

	/**
	 * Déformattage de la valeur
	 */
	private unFormatValue() {
		let value;

		//Lecture de la valeur saisie
		value = this.elementRef.nativeElement.value;

		if (value) {
			const decimalSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Decimal);
			const groupSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Group);
			//Suppression des séparateurs de groupe (milliers)
			let cleanValue = value.split(groupSymbol).join('');
			//Échappement du symbole décimal pour la regex de nettoyage
			const escapedDecimalSymbol = decimalSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`[^0-9-${escapedDecimalSymbol}]`, 'g');
			this._value = cleanValue.replace(regex, '');
		} else {
			this._value = '';
		}

		//Vérification de la valeur à afficher
		this.updateElementInput(value ? this._value : '');
	}

	/**
	 * Met à jour l'élément html avec la valeur
	 * */
	private updateElementInput(value) {
		this.elementRef.nativeElement.value = value;
	}

	/**
	 * Interception d'un changement de valeur
	 */
	@HostListener('input',['$event.target.value'])
	onInput(value) {
		const decimalSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Decimal);
		const groupSymbol = getLocaleNumberSymbol(this.localeId, NumberSymbol.Group);

		//Tolérance clavier : conversion automatique du point en virgule uniquement pour la locale française (où le séparateur décimal est la virgule)
		let normalizedValue = value || '';
		if (decimalSymbol === ',') {
			normalizedValue = normalizedValue.replace(/[.]/g, ',');
		}

		const escapedDecimalSymbol = decimalSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const escapedGroupSymbol = groupSymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

		//Regex qui autorise les chiffres, le signe moins, le symbole décimal et le symbole de groupe (milliers)
		const regex = new RegExp(`[^0-9-${escapedDecimalSymbol}${escapedGroupSymbol}]`, 'g');

		//Conserver uniquement les caractères autorisés pour la saisie
		this._value = normalizedValue ? normalizedValue.replace(regex, '') : '';

		//Met à jour l'élément avec la nouvelle valeur formatée
		this.updateElementInput(this._value);

		//Déclenchement de la modification
		let parsedValue: number | null = null;
		if (this._value) {
			//Suppression des séparateurs de milliers (y compris les différents types d'espaces si le symbole est un espace)
			let cleanValue = this._value;
			if (groupSymbol.trim() === '') {
				cleanValue = cleanValue.replace(/\s/g, '');
			} else {
				cleanValue = cleanValue.split(groupSymbol).join('');
			}
			//Remplacement du séparateur décimal par un point pour le parsing JS
			cleanValue = cleanValue.split(decimalSymbol).join('.');
			const num = Number(cleanValue);
			parsedValue = isNaN(num) ? null : num;
		}
		this.onChange(parsedValue);
	}

	/**
	 * Interception de la sortie du champ
	 */
	@HostListener('blur')
	onBlur() {
		this.isFocused = false;
		//Formattage de la valeur
		this.formatValue(this._value);
	}

	/**
	 * Interception d'un focus sur le champ
	 */
	@HostListener('focus')
	onFocus() {
		this.isFocused = true;
		//Déformattage de la valeur
		this.unFormatValue();
	}

	/**
	 * Ecriture de la valeur
	 */
	writeValue(value: any) {
		//Définition de la valeur
		this._value = value;

		//Formattage de la valeur
		this.formatValue(this._value);
	}

	/**
	 * Enregistrement d'un changement de valeur
	 */
	registerOnChange(fn: (value: any) => void) {
		//Définition de la fonction
		this.onChange = fn;
	}

	/**
	 * Interception d'un appui sur le composant
	 */
	registerOnTouched() {}

	/**
	 * Changement de valeur
	 */
	onChange(value: any): void {}
}